import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nProvider";

const ELECTION_DATE = new Date("2026-09-23T08:00:00+01:00");

function getTimeLeft() {
  const now = new Date();
  const diff = ELECTION_DATE.getTime() - now.getTime();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    total: diff,
  };
}

const labels = {
  fr: { days: "Jours", hours: "Heures", minutes: "Min", seconds: "Sec", title: "Élections législatives", date: "23 Sep 2026" },
  ar: { days: "أيام", hours: "ساعات", minutes: "دقائق", seconds: "ثواني", title: "الانتخابات التشريعية", date: "23 سبتمبر 2026" },
  zgh: { days: "ⵓⵙⵙⴰⵏ", hours: "ⵜⴰⵙⵔⴰⵜⵉⵏ", minutes: "ⵜⵓⵙⴷⴰⴷⵉⵏ", seconds: "ⵜⴰⵙⵉⵏⵜⵉⵏ", title: "ⵉⵙⵜⴰⵢⵏ ⵉⵙⵔⵜⴰⵏⵏ", date: "23 ⵛⵓⵜⴰⵏⴱⵉⵔ 2026" },
};

interface TimeBlockProps {
  value: number;
  label: string;
}

const TimeBlock = ({ value, label }: TimeBlockProps) => (
  <div className="flex flex-col items-center">
    <div className="relative w-12 h-12 flex items-center justify-center">
      {/* Glowing ring */}
      <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 48 48">
        <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
        <circle
          cx="24" cy="24" r="20" fill="none"
          stroke="rgba(255,200,60,0.85)"
          strokeWidth="3"
          strokeDasharray={`${Math.PI * 2 * 20}`}
          strokeDashoffset={`${Math.PI * 2 * 20 * (1 - value / (label === "Jours" || label === "أيام" || label === "ⵓⵙⵙⴰⵏ" ? 365 : label === "Sec" || label === "ثواني" || label === "ⵜⴰⵙⵉⵏⵜⵉⵏ" || label === "Min" || label === "دقائق" || label === "ⵜⵓⵙⴷⴰⴷⵉⵏ" ? 60 : 24))}`}
          strokeLinecap="round"
          className="transition-all duration-1000"
        />
      </svg>
      <span className="relative z-10 text-base font-display font-black text-accent tabular-nums leading-none">
        {String(value).padStart(2, "0")}
      </span>
    </div>
    <span className="text-[9px] font-body font-semibold text-primary-foreground/60 mt-1 uppercase tracking-wide leading-none">
      {label}
    </span>
  </div>
);

const ElectionCountdown = () => {
  const { lang } = useI18n();
  const l = labels[lang as keyof typeof labels] || labels.fr;
  const [time, setTime] = useState(getTimeLeft());

  useEffect(() => {
    const interval = setInterval(() => setTime(getTimeLeft()), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, delay: 0.5 }}
      className="flex flex-col items-start gap-1.5 px-4 py-3 rounded-2xl bg-white/8 backdrop-blur-md border border-white/15 shadow-lg"
    >
      <div className="flex items-center gap-1.5 w-full">
        <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse shrink-0" />
        <p className="text-[10px] font-body font-bold text-primary-foreground/70 uppercase tracking-widest leading-none whitespace-nowrap">
          {l.title}
        </p>
      </div>
      <p className="text-[10px] font-body text-accent font-bold tracking-wider">{l.date}</p>
      <div className="flex items-center gap-2">
        <TimeBlock value={time.days} label={l.days} />
        <span className="text-accent font-black text-lg leading-none mb-3">:</span>
        <TimeBlock value={time.hours} label={l.hours} />
        <span className="text-accent font-black text-lg leading-none mb-3">:</span>
        <TimeBlock value={time.minutes} label={l.minutes} />
        <span className="text-accent font-black text-lg leading-none mb-3">:</span>
        <TimeBlock value={time.seconds} label={l.seconds} />
      </div>
    </motion.div>
  );
};

export default ElectionCountdown;
